import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/do';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';

import {
  SkillSpecializationListItem,
  SkillSpecializationDeactivateParams,
  SkillSpecializationResponse,
  SkillSpecializationCreateRequest,
  SkillSpecializationCreationPermissions,
  CuratorAssignRequest,
  TheoryLevelRequest,
  TheoryTestGenerationRequest,
  PracticeLevelRequest,
  SkillAvailableCurator,
  SkillGroup,
  SkillPromptTemplateResponse,
  SkillCuratorAssignmentRequest,
  SkillCuratorAssignmentInput,
  SkillTestQuestion,
  SkillLevelStructureUpdateRequest,
  SkillRequirementDeleteCommand,
  SkillRequirementUpdateCommand,
  SkillUploadDocumentCommand,
  SkillLearningHttpError,
} from '../models/skill-learning.model';
import { SkillSpecializationApiService } from './skill-specialization-api.service';
import { SkillGroupApiService } from './skill-group-api.service';
import { AdminService } from 'app/services/admin.service';

const USER_STORAGE_KEY = 'User';
const HTTP_OK = 200;
const CURATOR_CONTEXT_INVALID = 'Curator department context is unavailable';

export class SkillDocumentRefreshError {
  constructor(readonly cause: SkillLearningHttpError) {}
}

@Injectable()
export class SkillSpecializationFacadeService {

  private specializationSaved$ = new Subject<void>();

  readonly onSpecializationSaved$: Observable<void> = this.specializationSaved$.asObservable();

  constructor(
    private specializationApi: SkillSpecializationApiService,
    private groupApi: SkillGroupApiService,
    private adminService: AdminService,
  ) {}

  fetchSpecializationList(departmentId?: string): Observable<SkillSpecializationListItem[]> {
    return this.specializationApi.fetchAllSpecializations(departmentId);
  }

  fetchSpecializationDetail(id: string, departmentId?: string): Observable<SkillSpecializationResponse> {
    return this.specializationApi.fetchSpecializationById(id, departmentId);
  }

  fetchAvailableSpecializations(departmentId?: string): Observable<SkillSpecializationListItem[]> {
    return this.specializationApi.fetchAvailableSpecializations(departmentId);
  }

  fetchCreationPermissions(): Observable<SkillSpecializationCreationPermissions> {
    return this.specializationApi.fetchCreationPermissions();
  }

  fetchTheoryDirectionsPrompt(id: string, level: number): Observable<SkillPromptTemplateResponse> {
    return this.specializationApi.fetchTheoryDirectionsPrompt(id, level);
  }

  fetchGroups(): Observable<SkillGroup[]> {
    return this.groupApi.fetchAllGroups();
  }

  fetchRoutingGroups(): Observable<SkillGroup[]> {
    const departments = this.adminService.getDepartmentGroups().map(response =>
      (<(SkillGroup & {type: string})[]>response.body).filter(group => !group.parentDepartmentId)
        .map(group => Object.assign({}, group, {groupType: group.type})));
    return Observable.forkJoin(this.fetchGroups(), departments).map(([managed, roots]) => {
      const ids = new Set<string>();
      return managed.concat(roots).filter(group => {
        if (ids.has(group.id)) { return false; }
        ids.add(group.id);
        return true;
      });
    });
  }

  fetchAvailableCurators(specializationId: string): Observable<SkillAvailableCurator[]> {
    const groupId = this.resolveCurrentUserGroupId();
    return this.groupApi.fetchAvailableCurators(groupId, specializationId);
  }

  fetchAvailableCuratorsForGroup(groupId: string, specializationId: string): Observable<SkillAvailableCurator[]> {
    return this.groupApi.fetchAvailableCurators(groupId, specializationId);
  }

  fetchGroupUsers(groupId: string): Observable<SkillAvailableCurator[]> {
    return this.groupApi.fetchGroupUsers(groupId);
  }

  fetchCuratorGroupUsers(groupId: string): Observable<SkillAvailableCurator[]> {
    return this.adminService.getGroupContext(groupId).switchMap(response => {
      const context = response.body;
      if (response.status !== HTTP_OK || !context || context.groupId !== groupId
        || context.parentDepartment === undefined) {
        return Observable.throw(new Error(CURATOR_CONTEXT_INVALID));
      }
      const parent = context.parentDepartment;
      if (parent && (!parent.id || parent.id === groupId)) {
        return Observable.throw(new Error(CURATOR_CONTEXT_INVALID));
      }
      const groupIds = parent ? [groupId, parent.id] : [groupId];
      return Observable.forkJoin(groupIds.map(id => this.groupApi.fetchGroupUsers(id)))
        .map(groups => this.mergeCuratorGroupUsers(groups, groupId));
    });
  }

  private mergeCuratorGroupUsers(groups: SkillAvailableCurator[][], groupId: string): SkillAvailableCurator[] {
    const ids = new Set<string>();
    const candidates: SkillAvailableCurator[] = [];
    groups.forEach(users => users.forEach(user => {
      if (user && user.id && !ids.has(user.id)) {
        ids.add(user.id);
        candidates.push(Object.assign({}, user, { localDepartment: user.groupId === groupId }));
      }
    }));
    return candidates;
  }

  createSpecialization(
    request: SkillSpecializationCreateRequest,
    curators: SkillCuratorAssignmentRequest[] | null,
  ): Observable<SkillSpecializationResponse> {
    const createRequest: SkillSpecializationCreateRequest = Object.assign({}, request);
    if (curators !== null) {
      createRequest.curators = curators || [];
    }
    return this.specializationApi.createSpecialization(createRequest);
  }

  updateGeneralInfo(id: string, request: SkillSpecializationCreateRequest): Observable<SkillSpecializationResponse> {
    return this.specializationApi.updateSpecialization(id, request)
      .do(() => this.specializationSaved$.next());
  }

  updateProbationRequirement(id: string, required: boolean): Observable<SkillSpecializationResponse> {
    return this.specializationApi.updateProbationRequirement(id, required)
      .do(() => this.specializationSaved$.next());
  }

  updateStructure(
    id: string,
    request: SkillLevelStructureUpdateRequest,
  ): Observable<SkillSpecializationResponse> {
    return this.specializationApi.updateStructure(id, request)
      .do(() => this.specializationSaved$.next());
  }

  updateRequirement(
    id: string,
    command: SkillRequirementUpdateCommand,
  ): Observable<SkillSpecializationResponse> {
    return this.specializationApi.updateRequirement(id, command.scope, command.request)
      .do(() => this.specializationSaved$.next());
  }

  deleteRequirement(
    id: string,
    command: SkillRequirementDeleteCommand,
  ): Observable<SkillSpecializationResponse> {
    return this.specializationApi.deleteRequirement(id, command.scope, command.params)
      .do(() => this.specializationSaved$.next());
  }

  updateSpecializationWithCurators(
    id: string,
    request: SkillSpecializationCreateRequest,
    curatorAssignment: SkillCuratorAssignmentInput,
  ): Observable<void> {
    return this.specializationApi.updateSpecialization(id, request)
      .switchMap(() => {
        const assignRequest = this.buildCuratorAssignRequest(curatorAssignment);
        return this.specializationApi.assignCurators(id, assignRequest);
      })
      .do(() => this.specializationSaved$.next());
  }

  assignCurators(
    id: string,
    curatorAssignment: SkillCuratorAssignmentInput,
    departmentId?: string,
  ): Observable<SkillSpecializationResponse> {
    const assignRequest = this.buildCuratorAssignRequest(curatorAssignment);
    return this.specializationApi.assignCurators(id, assignRequest)
      .switchMap(() => this.fetchSpecializationDetail(id, departmentId))
      .do(() => this.specializationSaved$.next());
  }

  removeCurator(id: string, curatorId: string, departmentId?: string): Observable<void> {
    return this.specializationApi.removeCurator(id, curatorId, departmentId)
      .do(() => this.specializationSaved$.next());
  }

  updateTheoryLevel(
    id: string,
    level: number,
    request: TheoryLevelRequest,
  ): Observable<SkillSpecializationResponse> {
    return this.specializationApi.updateTheoryLevel(id, level, request)
      .do(() => this.specializationSaved$.next());
  }

  generateTheoryTestQuestions(
    id: string,
    level: number,
    request: TheoryTestGenerationRequest,
  ): Observable<SkillTestQuestion[]> {
    return this.specializationApi.generateTheoryTestQuestions(id, level, request);
  }

  updatePracticeLevel(
    id: string,
    level: number,
    request: PracticeLevelRequest,
  ): Observable<SkillSpecializationResponse> {
    return this.specializationApi.updatePracticeLevel(id, level, request)
      .do(() => this.specializationSaved$.next());
  }

  uploadLevelDocument(
    id: string,
    command: SkillUploadDocumentCommand,
    departmentId?: string,
  ): Observable<SkillSpecializationResponse> {
    return this.specializationApi.uploadLevelDocument(id, command)
      .switchMap(() => this.fetchSpecializationDetail(id, departmentId)
        .catch(error => Observable.throw(new SkillDocumentRefreshError(error))))
      .do(() => this.specializationSaved$.next());
  }

  buildDocumentDownloadUrl(id: string, level: number): string {
    return this.specializationApi.buildLevelDocumentDownloadUrl(id, level);
  }

  deleteSpecialization(id: string, params: SkillSpecializationDeactivateParams): Observable<void> {
    return this.specializationApi.deactivateSpecialization(id, params);
  }

  createFillTask(id: string): Observable<SkillSpecializationResponse> {
    return this.specializationApi.createFillTask(id)
      .do(() => this.specializationSaved$.next());
  }

  private resolveCurrentUserGroupId(): string {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) {
      return '';
    }
    const user = JSON.parse(raw);
    if (user && user.mainGroup) {
      return user.mainGroup;
    }
    return '';
  }

  private buildCuratorAssignRequest(input: SkillCuratorAssignmentInput): CuratorAssignRequest {
    if (Array.isArray(input)) {
      return { curators: <SkillCuratorAssignmentRequest[]>input };
    }
    const request = <CuratorAssignRequest>input;
    const result: CuratorAssignRequest = { curators: request.curators || [] };
    if (request.responsibleGroupId) {
      result.responsibleGroupId = request.responsibleGroupId;
    }
    return result;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/services/skill-specialization-facade.service.ts